/* ========================================
   Proadsy Scroll Animations — GSAP + ScrollTrigger
   v4.0 (2026-08-06): unified entrance system.
   ONE pattern sitewide: fade + 8px rise, 450ms, stagger 80ms, once (P3).
   - fromTo only runs onEnter → nothing is hidden before its trigger; if GSAP
     fails to load, the failsafe below force-shows every CSS-pre-hidden element
     (D1 law: animation never gates visibility).
   - prefers-reduced-motion: reveals / count-ups / hover lift are skipped.
   - Parallax block removed (strategy: no parallax; .hero-visual is the YMMT
     tool card now — scrub-scaling its result images was wrong).
   ======================================== */
(function() {
  'use strict';

  if (typeof gsap === 'undefined' || typeof ScrollTrigger === 'undefined') {
    /* FAILSAFE 2026-08-13: cdnjs blocked/slow must never blind the page —
       CSS unconditionally pre-hides these selectors, so force them visible. */
    var failsafeSel = '.reveal, .section-label, .section-title, .section-desc, .hero-badge, .hero h1, .hero-sub, .ymmt-box, .hero-visual';
    document.querySelectorAll(failsafeSel).forEach(function(el) {
      el.style.opacity = '1';
      el.style.transform = 'none';
    });
    return;
  }
  gsap.registerPlugin(ScrollTrigger);

  var reduceMotion = window.matchMedia && window.matchMedia('(prefers-reduced-motion: reduce)').matches;

  /* --- Unified config (≈ tokens --pa1-ease / --pa1-d-slow) --- */
  var EASE = 'expo.out';
  var DURATION = 0.45;
  var STAGGER_GAP = 0.08;
  var REVEAL_Y = 8;

  /* --- Hero entrance (above the fold, no scroll trigger) --- */
  if (!reduceMotion) {
    var hero = document.querySelector('.hero');
    if (hero) {
      var tl = gsap.timeline({ defaults: { ease: EASE } });
      function heroFromTo(selector, fromVars, toVars, position) {
        if (document.querySelector(selector)) {
          tl.fromTo(selector, fromVars, toVars, position);
        }
      }
      heroFromTo('.hero-badge', { opacity: 0, y: 20 }, { opacity: 1, y: 0, duration: 0.6 }, 0.1);
      heroFromTo('.hero h1', { opacity: 0, y: 40 }, { opacity: 1, y: 0, duration: 0.9 }, 0.2);
      heroFromTo('.hero-sub', { opacity: 0, y: 30 }, { opacity: 1, y: 0, duration: 0.8 }, 0.4);
      heroFromTo('.ymmt-box', { opacity: 0, y: 30 }, { opacity: 1, y: 0, duration: 0.8 }, 0.55);
      heroFromTo('.hero-visual', { opacity: 0, y: 50, scale: 0.97 }, { opacity: 1, y: 0, scale: 1, duration: 1.2 }, 0.35);
    }
  }

  /* --- Single entrance pattern: fade + 8px, 450ms, once --- */
  function revealEl(el, delay) {
    ScrollTrigger.create({
      trigger: el,
      start: 'top 88%',
      once: true,
      onEnter: function() {
        gsap.fromTo(el,
          { opacity: 0, y: REVEAL_Y },
          { opacity: 1, y: 0, duration: DURATION, delay: delay || 0, ease: EASE }
        );
      }
    });
  }

  if (!reduceMotion) {

    /* --- .reveal elements (v1 pages: our-story / product-page) --- */
    gsap.utils.toArray('.reveal').forEach(function(el) {
      if (el.closest && el.closest('.hero')) return;

      var delay = 0;
      if (el.classList.contains('d2')) delay = STAGGER_GAP;
      else if (el.classList.contains('d3')) delay = STAGGER_GAP * 2;
      else if (el.classList.contains('d4')) delay = STAGGER_GAP * 3;

      revealEl(el, delay);
    });

    /* --- Section headings (v1 auto-reveal) --- */
    document.querySelectorAll('.section-label, .section-title, .section-desc').forEach(function(el) {
      if (el.closest && el.closest('.hero')) return;
      if (el.classList.contains('reveal')) return;

      var siblingIndex = 0;
      var prev = el.previousElementSibling;
      while (prev) {
        if (prev.classList.contains('section-label') || prev.classList.contains('section-title') || prev.classList.contains('section-desc')) siblingIndex++;
        prev = prev.previousElementSibling;
      }

      revealEl(el, siblingIndex * STAGGER_GAP);
    });

    /* --- Unified block entrance: v2 homepage / PDP / collection sections.
         One selector list, one pattern, stagger capped at 5 steps.
         v1 selectors kept at the end (no-op where absent). --- */
    var blockSelectors = [
      '.ps-duo__card',     // product-lines
      '.ps-bs__card',      // best-sellers
      '.ps-ev__card',      // ev-editions
      '.ro__card',         // real owners wall
      '.pa-brand-card',    // brand grid
      '.ps-guar__card',    // guarantee
      '.ps-s2s__card',     // scan-to-shield
      '.psw__col',         // speedwar columns
      '.ps-cta__inner',    // final cta
      /* v1 fallbacks (no-op on v2 pages) */
      '.trust-bar',
      '.product-line-card',
      '.comp-col',
      '.bs-card',
      '.review-card',
      '.guarantee-feature',
      '.final-cta'
    ];

    blockSelectors.forEach(function(sel) {
      gsap.utils.toArray(sel).forEach(function(el, i) {
        if (el.classList.contains('reveal')) return;
        revealEl(el, Math.min(i, 5) * STAGGER_GAP);
      });
    });

    /* --- v2 section heads (eyebrow + title + lead) --- */
    gsap.utils.toArray('.ps-duo__head, .ps-bs__head, .ps-ev__head, .ps-guar__head, .ro__head, .ps-s2s__head, .psw__head').forEach(function(el) {
      revealEl(el, 0);
    });

    /* --- Comparison cards (compare-vs): same unified pattern, second card one step later --- */
    var compThem = document.querySelector('.compare-card.them');
    var compUs = document.querySelector('.compare-card.us');
    if (compThem) {
      revealEl(compThem, 0);
      if (compUs) revealEl(compUs, STAGGER_GAP);
    }

    /* --- Product cards hover lift (non-touch) --- */
    if (!('ontouchstart' in window)) {
      document.querySelectorAll('.product-card, .line-card, .review-card').forEach(function(card) {
        card.addEventListener('mouseenter', function() {
          gsap.to(card, { y: -6, duration: 0.4, ease: 'power2.out' });
        });
        card.addEventListener('mouseleave', function() {
          gsap.to(card, { y: 0, duration: 0.5, ease: 'power2.out' });
        });
      });
    }
  }

  /* --- Stats Counter Animation (v1 classes — dead on v2 pages, kept for our-story) --- */
  if (!reduceMotion) {
    var statsSection = document.querySelector('.stats');
    if (statsSection) {
      ScrollTrigger.create({
        trigger: statsSection,
        start: 'top 70%',
        once: true,
        onEnter: function() { animateCounters(statsSection); }
      });
    }

    /* --- Scenario Proof number animation (v1 classes — dead on v2 pages, kept) --- */
    document.querySelectorAll('.scenario-card').forEach(function(card) {
      ScrollTrigger.create({
        trigger: card,
        start: 'top 75%',
        once: true,
        onEnter: function() { animateScenarioNumbers(card); }
      });
    });
  }

  function animateCounters(container) {
    container.querySelectorAll('.number').forEach(function(el) {
      var raw = el.textContent.replace(/,/g, '').trim();
      var target = parseInt(raw, 10);
      if (isNaN(target) || target <= 0) return;

      var obj = { val: 0 };
      gsap.to(obj, {
        val: target,
        duration: 2.2,
        ease: 'power3.out',
        onUpdate: function() {
          el.textContent = Math.round(obj.val).toLocaleString();
        }
      });
    });
  }

  function animateScenarioNumbers(card) {
    card.querySelectorAll('.scenario-stat-value').forEach(function(el) {
      var raw = el.textContent.replace(/[°,]/g, '').trim();
      var target = parseInt(raw, 10);
      if (isNaN(target)) return;
      var suffix = el.textContent.indexOf('°') !== -1 ? '°' : '';

      var obj = { val: 0 };
      gsap.to(obj, {
        val: target,
        duration: 1.8,
        ease: 'power3.out',
        onUpdate: function() {
          el.textContent = Math.round(obj.val) + suffix;
        }
      });
    });
  }

  /* --- v2 data-story count-up (scenario-proof 157→98/47, speedwar 3/28/15) — 2026-07-06 A7.
     Reads the number straight from the element text (no section markup change) and only mutates
     the numeric text node, so <small>s</small> unit suffixes survive. Numbers render as their
     final value with no JS → never gates visibility (D1 law). */
  if (!reduceMotion) {
    document.querySelectorAll('.ps-proof__num-val, .psw__time').forEach(function(el) {
      ScrollTrigger.create({
        trigger: el, start: 'top 85%', once: true,
        onEnter: function() { countUpEl(el, 1.4); }
      });
    });
  }

  function countUpEl(el, dur) {
    var numNode = null, i;
    for (i = 0; i < el.childNodes.length; i++) {
      if (el.childNodes[i].nodeType === 3 && /\d/.test(el.childNodes[i].nodeValue)) { numNode = el.childNodes[i]; break; }
    }
    var whole = numNode ? numNode.nodeValue : el.textContent;
    var m = whole.match(/(\d[\d,]*)/);
    if (!m) return;
    var target = parseInt(m[1].replace(/,/g, ''), 10);
    if (!target) return; // skip 0 (e.g. "0 surface marks" stays static)
    var prefix = whole.slice(0, m.index), suffix = whole.slice(m.index + m[1].length);
    var obj = { val: 0 };
    gsap.to(obj, {
      val: target, duration: dur, ease: 'power3.out',
      onUpdate: function() {
        var t = prefix + Math.round(obj.val).toLocaleString() + suffix;
        if (numNode) { numNode.nodeValue = t; } else { el.textContent = t; }
      }
    });
  }

})();
